import { useCallback, useEffect, useState } from "react";
import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import { formatDateTime } from "../Common/DateFormat";
import CustomSnackbar from "../Common/snackBar";
import renderStatusBadge from "../../utils/badges";
import { getMasterTenantLogsAction } from "../../Actions/TenantActions";
import { ITEMS_PER_PAGE_OPTIONS } from "../../config";
import DataTable from "../../components/DataTable/DataTable"

const getDefaultDateRange = () => {
	const end = new Date();
	const start = new Date();
	start.setMonth(start.getMonth() - 1);

	return { from: start, to: end };
};


const MasterMailLogsSection = () => {
	const [logsData, setLogsData] = useState([]);
	const defaultRange = getDefaultDateRange();
	const [filterValues, setFilterValues] = useState({
		status: "all",
		search: "",
		dateRange: defaultRange,
	});
	const [currentPage, setCurrentPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE_OPTIONS[0]);
	const [totalRecords, setTotalRecords] = useState(0);
	const [loading, setLoading] = useState(true);
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success",
	});
	const startDate = filterValues.dateRange?.from
		? new Date(
			Date.UTC(
				filterValues.dateRange.from.getFullYear(),
				filterValues.dateRange.from.getMonth(),
				filterValues.dateRange.from.getDate(),
				0,
				0,
				0,
				0
			)
		).toISOString()
		: null;

	const endDate = filterValues.dateRange?.to
		? new Date(
			Date.UTC(
				filterValues.dateRange.to.getFullYear(),
				filterValues.dateRange.to.getMonth(),
				filterValues.dateRange.to.getDate(),
				23,
				59,
				59,
				999
			)
		).toISOString()
		: null;

	const fetchTenantLogs = useCallback(
		async (isFilterAction = false) => {
			setLoading(true);
			try {
				const res = await getMasterTenantLogsAction(
					currentPage,
					rowsPerPage,
					filterValues.status,
					startDate,
					endDate,
					filterValues.search

				);

				setLogsData(res.data.data.rows);
				setTotalRecords(res.data.data.count);
			} catch (error) {
				console.error("Fetch error:", error);
				setSnackbar({
					open: true,
					message: error?.response?.data?.message || "Failed to fetch tenant logs",
					severity: "error",
				});
			} finally {
				setLoading(false);
			}
		},
		[currentPage, rowsPerPage, filterValues]
	);

	useEffect(() => {
		fetchTenantLogs();
	}, [fetchTenantLogs]);

	const handleFilterApply = (values) => {
		setCurrentPage(1);
		setFilterValues(values);
	};

	const handleFilterReset = () => {
		setCurrentPage(1);

		setFilterValues({
			status: "all",
			search: "",
			dateRange: defaultRange,
		});
	};

	const columns = [
		{
			field: "tenant_name",
			headerName: "Tenant Name",
			flex: 1,
			minWidth: 150,
		},
		{
			field: "domain_url",
			headerName: "Domain URL",
			flex: 1,
			minWidth: 200,
		},
		{
			field: "status",
			headerName: "Status",
			minWidth: 120,
			renderCell: ({ row }) =>
				renderStatusBadge(
					{
						SUCCESS: { title: "Success", status: "success" },
						FAILED: { title: "Failed", status: "danger" },
						PENDING: { title: "Pending", status: "warning" },
					}[row.status]
				),
		},
		{
			field: "tenant_response",
			headerName: "Response",
			flex: 1,
			minWidth: 200,
		},
		{
			field: "created_At",
			headerName: "Date And Time",
			minWidth: 200,
			renderCell: ({ row }) => formatDateTime(row.created_At, true),
		},
	];


	const filters = [
		{
			name: "search",
			type: "search",
			placeholder: "Search by tenant name, domain or response",
		},
		{
			name: "status",
			label: "Status",
			type: "select",
			options: [
				{ label: "All Status", value: "all" },
				{ label: "Success", value: "SUCCESS" },
				{ label: "Failed", value: "FAILED" },
				{ label: "Pending", value: "PENDING" },
			],
		},
		{
			name: "dateRange",
			label: "Date Range",
			type: "dateRange",
		},
	];

	return (
		<>
			<div id="kt_content_container">
				<BreadcrumbsComponent />

				<DataTable
					rows={logsData}
					columns={columns}
					loading={loading}
					totalCount={totalRecords}
					page={currentPage}
					pageSize={rowsPerPage}
					getRowId={(row) => row.id}
					filters={filters}
					filterValues={filterValues}
					onFilterApply={handleFilterApply}
					onFilterReset={handleFilterReset}
					onPageChange={setCurrentPage}
					onPageSizeChange={(size) => {
						setRowsPerPage(size);
						setCurrentPage(1);
					}}
					showPagination={true}
					showRowsPerPage={true}
				/>

				<CustomSnackbar
					{...snackbar}
					onClose={() => setSnackbar({ ...snackbar, open: false })}
				/>
			</div>
		</>
	);
};

export default MasterMailLogsSection;